import logger from '../services/logger';

// Track page views and navigation
let lastPageUrl = window.location.pathname;
let pageStartTime = Date.now();

// Store event listener references for cleanup
let eventListeners = {
  click: null,
  error: null,
  unhandledrejection: null,
  popstate: null
};

let isEventTrackingInitialized = false;

/**
 * Initialize event tracking with proper cleanup
 * CRITICAL: Uses event listener removal to prevent memory leaks
 */
export function initializeEventTracking() {
  // Prevent duplicate initialization
  if (isEventTrackingInitialized) {
    console.log('Event tracking already initialized, skipping...');
    return;
  }

  isEventTrackingInitialized = true;

  // Only enable click tracking if logging is enabled
  if (logger.isLoggingEnabled && logger.isLoggingEnabled()) {
    // 1. Track Click Events (DEBOUNCED to reduce memory impact)
    let clickTimeout;
    const clickHandler = (e) => {
      // Clear previous timeout
      if (clickTimeout) clearTimeout(clickTimeout);
      
      // Debounce click tracking to max 1 per 100ms
      clickTimeout = setTimeout(() => {
        try {
          const target = e.target;
          const elementId = target.id || target && target.closest('[id]') && target.closest('[id]').id || '';
          const elementClass = target.className || '';
          const pageUrl = window.location.pathname;
          
          if (logger.logClick) {
            logger.logClick(
              elementId,
              typeof elementClass === 'string' ? elementClass : '',
              pageUrl,
              e.clientX,
              e.clientY
            );
          }
        } catch (error) {
          // Silently ignore to prevent memory buildup from error logging
        }
      }, 100);
    };
    
    document.addEventListener('click', clickHandler, true);
    eventListeners.click = { target: document, handler: clickHandler, options: true };
  }

  // 2. Track Global Errors (only critical errors, not all)
  const errorHandler = (e) => {
    try {
      // Skip non-critical errors to reduce memory impact
      if (e.message &&
          (e.message.includes('Script error') ||
           e.message.includes('ResizeObserver') ||
           e.message.includes('Network request') ||
           !e.error)) {
        return;
      }

      if (logger.logError) {
        logger.logError(
          e.message,
          e.error && e.error.stack || '',
          e.filename,
          e.lineno,
          e.colno
        );
      }
    } catch (error) {
      // Silently ignore
    }
  };

  window.addEventListener('error', errorHandler);
  eventListeners.error = { target: window, handler: errorHandler };

  // 3. Track Unhandled Promise Rejections (only if logging enabled)
  if (logger.isLoggingEnabled && logger.isLoggingEnabled()) {
    const rejectionHandler = (e) => {
      try {
        if (logger.logError) {
          logger.logError(
            'Unhandled Promise Rejection: ' + (e.reason || 'Unknown'),
            (e.reason && e.reason.stack) || '',
            window.location.pathname,
            0,
            0
          );
        }
      } catch (error) {
        // Silently ignore
      }
    };

    window.addEventListener('unhandledrejection', rejectionHandler);
    eventListeners.unhandledrejection = { target: window, handler: rejectionHandler };
  }

  // 4. Track Navigation Changes (for SPA) - Only if logging enabled
  if (logger.isLoggingEnabled && logger.isLoggingEnabled()) {
    const popstateHandler = () => {
      try {
        const currentUrl = window.location.pathname;
        const duration = Date.now() - pageStartTime;
        
        if (logger.logPageView) {
          logger.logPageView(
            lastPageUrl,
            currentUrl,
            duration,
            document.referrer
          );
        }
        
        lastPageUrl = currentUrl;
        pageStartTime = Date.now();
      } catch (error) {
        // Silently ignore
      }
    };

    window.addEventListener('popstate', popstateHandler);
    eventListeners.popstate = { target: window, handler: popstateHandler };
  }

  // 5. Clean up listeners on page unload to prevent memory leaks
  const unloadHandler = () => {
    cleanupEventTracking();
  };

  window.addEventListener('beforeunload', unloadHandler);

  console.log('Event tracking initialized with cleanup handlers');
}

/**
 * Clean up all event tracking listeners to prevent memory leaks
 * Should be called on page unload or when logging is disabled
 */
export function cleanupEventTracking() {
  Object.keys(eventListeners).forEach(eventName => {
    const listener = eventListeners[eventName];
    if (listener) {
      const { target, handler, options } = listener;
      if (target && handler) {
        target.removeEventListener(eventName, handler, options || false);
      }
    }
  });

  eventListeners = {
    click: null,
    error: null,
    unhandledrejection: null,
    popstate: null
  };

  isEventTrackingInitialized = false;
  console.log('Event tracking cleaned up - listeners removed');
}

// Helper function to track route changes in React Router
export function trackRouteChange(fromPath, toPath) {
  // Only track if logging is enabled
  if (logger && logger.isLoggingEnabled && !logger.isLoggingEnabled()) {
    return;
  }

  try {
    const duration = Date.now() - pageStartTime;
    
    if (logger && logger.logPageView) {
      logger.logPageView(
        fromPath,
        toPath,
        duration,
        document.referrer
      );
    }
    
    lastPageUrl = toPath;
    pageStartTime = Date.now();
  } catch (error) {
    // Silently ignore
  }
}

// Helper function to track form submissions - Safe
export function trackFormSubmit(formId, formName, fieldCount) {
  // Only track if logging is enabled
  if (logger && logger.isLoggingEnabled && !logger.isLoggingEnabled()) {
    return;
  }

  try {
    if (logger && logger.logFormSubmit) {
      logger.logFormSubmit(
        formId,
        formName,
        window.location.pathname,
        fieldCount
      );
    }
  } catch (error) {
    // Silently ignore
  }
}

// Export logger for direct use
export { logger };
